import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useEffect, useRef, useMemo } from "react";
import CloseIcon from "@mui/icons-material/Close";
import { Check } from "lucide-react";
import { Box, Chip, Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { fetchGlobalClientsAsync, enablePresentationModeAsync } from "../redux/presentationSlice";
import { toast } from "./Toast/ToastUtils";
import { Dropdown } from "./FormInputs";
// Selected-client chip colours (background / text / x icon), cycled like the chips of the shared MultiSelectInput.
const CHIP_STYLES = [
    { bg: "#F9F5FF", color: "#6941C6", icon: "#9E77ED" },
    { bg: "#EFF8FF", color: "#175CD3", icon: "#2E90FA" },
    { bg: "#EEF4FF", color: "#3538CD", icon: "#6172F3" },
];
// Chips shown in the new Dropdown before the rest collapse into a "+N" chip (same as the old control's maxDisplay).
const MAX_CHIPS = 3;
export const PresentationModals = ({ openConfirmModal, setOpenConfirmModal, openSelectStoreModal, setOpenSelectStoreModal, isClientListingPage }) => {
    const dispatch = useDispatch();
    const token = useSelector((state) => state.auth.token);
    const { globalClients, loading } = useSelector((state) => state.presentation);
    const [selectedClients, setSelectedClients] = useState([]);
    const autoSelectAppliedRef = useRef(false);
    useEffect(() => {
        if (openSelectStoreModal) {
            dispatch(fetchGlobalClientsAsync(token));
        }
    }, [openSelectStoreModal, dispatch, token]);
    // Reset so the auto-select re-evaluates the next time the modal is opened.
    useEffect(() => {
        if (!openSelectStoreModal) {
            autoSelectAppliedRef.current = false;
            setSelectedClients([]);
        }
    }, [openSelectStoreModal]);
    // New Dropdown chips: MAX_CHIPS chips + a "+N" chip; clicking "+N" shows them all until the user clicks away.
    const [showAllChips, setShowAllChips] = useState(false);
    const newDropdownRef = useRef(null);
    useEffect(() => {
        if (!openSelectStoreModal || selectedClients.length <= MAX_CHIPS)
            setShowAllChips(false);
    }, [openSelectStoreModal, selectedClients.length]);
    useEffect(() => {
        if (!showAllChips)
            return;
        const onMouseDown = (event) => {
            if (!newDropdownRef.current?.contains(event.target))
                setShowAllChips(false);
        };
        document.addEventListener("mousedown", onMouseDown);
        return () => document.removeEventListener("mousedown", onMouseDown);
    }, [showAllChips]);
    const allClients = useMemo(() => {
        return (globalClients || []).flatMap((client) => [
            client,
            ...(client.group_accounts || []).map((group) => ({
                ...group,
                type: client.type
            }))
        ]);
    }, [globalClients]);
    // If a client is already selected on the current page (tracked globally via
    // localStorage by useClientSelection), pre-select that same client here.
    // If no client is currently selected anywhere (Presentation Mode opened
    // directly), leave selection empty as before.
    useEffect(() => {
        if (!openSelectStoreModal || autoSelectAppliedRef.current || globalClients.length === 0) {
            return;
        }
        autoSelectAppliedRef.current = true;
        // If we are on a listing page, don't auto-select.
        if (isClientListingPage)
            return;
        const currentClientId = localStorage.getItem('globalSelectedGroupAccountId') || localStorage.getItem('globalSelectedClientId');
        if (!currentClientId)
            return;
        const matchedClient = allClients.find((c) => c._id === currentClientId);
        if (matchedClient) {
            setSelectedClients([matchedClient]);
        }
    }, [openSelectStoreModal, globalClients, allClients]);
    const handleConfirmProceed = () => {
        setOpenConfirmModal(false);
        setOpenSelectStoreModal(true);
    };
    const handleEnablePresentation = async () => {
        if (selectedClients.length === 0) {
            toast.warning("Please select at least one client.");
            return;
        }
        let presentation_role_name = "Presentation Mode (Full Access)";
        let hasPaidMedia = false;
        let hasAffiliate = false;
        selectedClients.forEach(c => {
            if (c.type?.includes('paid_media'))
                hasPaidMedia = true;
            if (c.type?.includes('affiliate'))
                hasAffiliate = true;
        });
        if (hasPaidMedia && !hasAffiliate) {
            presentation_role_name = "Presentation Mode (Paid Media Access)";
        }
        else if (!hasPaidMedia && hasAffiliate) {
            presentation_role_name = "Presentation Mode (Affiliate Access)";
        }
        const clientIds = selectedClients.map(c => c._id);
        try {
            const resultAction = await dispatch(enablePresentationModeAsync({
                token,
                presentationMode: true,
                presentationClients: clientIds,
                presentation_role_name
            }));
            if (enablePresentationModeAsync.fulfilled.match(resultAction)) {
                toast.success("Presentation Mode Enabled");
                setOpenSelectStoreModal(false);
                setSelectedClients([]);
                // Reload window to apply new global permissions smoothly
                window.location.reload();
            }
            else {
                toast.error(resultAction.payload || "Failed to enable Presentation Mode");
            }
        }
        catch (error) {
            toast.error(error?.message || "Failed to enable Presentation Mode");
        }
    };
    const clientOptions = (globalClients || [])
        .flatMap((client) => {
        const parent = {
            value: client._id,
            label: client.name,
            isParent: true
        };
        const groups = (client.group_accounts || []).map((group) => ({
            value: group._id,
            label: `${group.name}`,
            parentId: client._id,
            isGroup: true
        }));
        return [parent, ...groups];
    })
        .sort((a, b) => a.label.localeCompare(b.label));
    // One row at the top of the new Dropdown's menu: "Select All" on the left, a pink "Clear All" on the right. The shared
    // Dropdown keeps its search text inside, so Select All picks every client, not only the ones matching the search.
    const allClientsSelected = allClients.length > 0 && selectedClients.length === allClients.length;
    const hasSelection = selectedClients.length > 0;
    const dropdownMenuHeader = (_jsxs("div", { style: {
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "10px 14px",
            fontSize: "14px",
            borderBottom: "1px solid #f5f5f5",
        }, children: [_jsxs("div", { style: { display: "flex", alignItems: "center", gap: "10px", cursor: "pointer", color: "#344054" }, onClick: () => setSelectedClients(allClients), children: [_jsx("span", { className: `dropdown-checkbox ${allClientsSelected ? "checked" : ""}`, children: allClientsSelected && _jsx(Check, { size: 12 }) }), _jsx("span", { className: "all-Font-Sizes", children: "Select All Clients" })] }), _jsx("span", { className: "all-Font-Sizes", style: {
                    color: "#ff008e",
                    fontWeight: 500,
                    cursor: hasSelection ? "pointer" : "not-allowed",
                    opacity: hasSelection ? 1 : 0.5,
                }, onClick: () => {
                    if (hasSelection)
                        setSelectedClients([]);
                }, children: "Clear All" })] }));
    // The new Dropdown shows the picked clients as removable chips (undefined = it shows its placeholder).
    const visibleClients = showAllChips ? selectedClients : selectedClients.slice(0, MAX_CHIPS);
    const hiddenCount = selectedClients.length - visibleClients.length;
    const selectedChips = selectedClients.length > 0 ? (_jsxs(Box, { sx: {
            display: "flex",
            flexWrap: "wrap",
            gap: 0.5,
            whiteSpace: "normal",
            maxHeight: showAllChips ? "120px" : "auto",
            overflowY: showAllChips && selectedClients.length > 6 ? "auto" : "visible",
        }, children: [visibleClients.map((client, index) => {
                const style = CHIP_STYLES[index % CHIP_STYLES.length];
                return (_jsx(Chip, { label: client.name, 
                    // Clicks on a chip must not open / close the dropdown behind it.
                    onClick: (e) => e.stopPropagation(), onDelete: (e) => {
                        e.stopPropagation();
                        setSelectedClients((prev) => prev.filter((c) => c._id !== client._id));
                    }, deleteIcon: _jsx(CloseIcon, { style: { fontSize: "12px" } }), sx: {
                        fontSize: "14px",
                        fontWeight: 500,
                        height: "24px",
                        borderRadius: "16px",
                        cursor: "default",
                        flexShrink: 0,
                        textTransform: "capitalize",
                        // App.css paints every clickable filled chip pink (.MuiButtonBase-root.MuiChip-root.MuiChip-filled),
                        // so these selectors repeat those classes to win and keep each chip's own colour.
                        "&.MuiButtonBase-root.MuiChip-root.MuiChip-filled": { backgroundColor: style.bg },
                        "&.MuiButtonBase-root.MuiChip-root.MuiChip-filled:hover": { backgroundColor: style.bg },
                        "&.MuiButtonBase-root.MuiChip-root.MuiChip-filled .MuiChip-label": { color: style.color },
                        "& .MuiChip-deleteIcon": { color: style.icon, fontSize: "12px", cursor: "pointer", "&:hover": { color: style.icon } },
                    } }, client._id));
            }), hiddenCount > 0 && (_jsx(Chip, { label: `+${hiddenCount}`, onClick: (e) => {
                    e.stopPropagation();
                    setShowAllChips(true);
                }, sx: {
                    fontSize: "14px",
                    fontWeight: 500,
                    height: "24px",
                    borderRadius: "16px",
                    cursor: "pointer",
                    flexShrink: 0,
                    "&.MuiButtonBase-root.MuiChip-root.MuiChip-filled": { backgroundColor: "#F5F5F5" },
                    "&.MuiButtonBase-root.MuiChip-root.MuiChip-filled:hover": { backgroundColor: "#E5E5E5" },
                    "&.MuiButtonBase-root.MuiChip-root.MuiChip-filled .MuiChip-label": { color: "#414651" },
                } }))] })) : undefined;
    return (_jsxs(_Fragment, { children: [_jsxs(Dialog, { open: openConfirmModal, onClose: () => setOpenConfirmModal(false), maxWidth: "sm", fullWidth: true, children: [_jsx(DialogTitle, { sx: { fontWeight: 600 }, children: "Enable Presentation Mode" }), _jsx(DialogContent, { children: _jsx(Typography, { children: "Are you sure you want to enable the presentation mode?" }) }), _jsxs(DialogActions, { sx: { p: 2, pt: 0 }, children: [_jsx(Button, { onClick: () => setOpenConfirmModal(false), color: "inherit", children: "Cancel" }), _jsx(Button, { onClick: handleConfirmProceed, variant: "contained", color: "primary", children: "Yes, Proceed" })] })] }), _jsxs(Dialog, { open: openSelectStoreModal, onClose: () => setOpenSelectStoreModal(false), maxWidth: "sm", fullWidth: true, slotProps: { paper: { sx: { overflow: "visible" } } }, children: [_jsx(DialogTitle, { sx: { fontWeight: 600, py: 1 }, children: "Select Store" }), _jsx(DialogContent, { sx: { overflow: "visible" }, children: _jsx(Box, { ref: newDropdownRef, children: _jsx(Dropdown, { label: "Select Clients", value: selectedClients.map((c) => c._id), onChange: (val) => {
                                    const ids = Array.isArray(val) ? val : [];
                                    setSelectedClients(allClients.filter((c) => ids.includes(c._id)));
                                }, options: clientOptions, placeholder: "Select Clients", displayValue: selectedChips, menuHeader: dropdownMenuHeader, loading: loading, multiple: true, searchable: true }) }) }), _jsxs(DialogActions, { sx: { p: 2, pt: 0 }, children: [_jsx(Button, { onClick: () => { setOpenSelectStoreModal(false); setSelectedClients([]); }, color: "inherit", disabled: loading, children: "Cancel" }), _jsx(Button, { onClick: handleEnablePresentation, variant: "contained", color: "primary", disabled: loading, children: loading ? "Enabling..." : "Enable" })] })] })] }));
};
